import { Role } from 'API';
import { ColumnFilter, ColumnFilterMap, FilterOption } from 'util/filtering/models';

export interface UserFilterConfig {
  key: string;
  backendField: string;
  type: 'string' | 'enum' | 'exists' | 'visibility';
  name: string;
  supportsExclude?: boolean;
  replacesDefaultFilter?: boolean;
  staticOptions?: FilterOption[];
  searchType?: 'projects' | 'glCodes';
  transform?: (values: FilterOption[], rule: string) => Record<string, unknown>[];
}

export interface FilterBuildResult {
  andConditions: Array<{ or?: Array<Record<string, unknown>>; and?: Array<Record<string, unknown>> }>;
  replacesDefault: boolean;
}

export const DEFAULT_USER_FILTER = {
  and: [
    {
      or: [{ user_id: { exists: true } }, { user_id: { exists: false } }],
    },
  ],
};

export const DEFAULT_USER_FILTER_AND = DEFAULT_USER_FILTER.and;

export const USER_FILTERS: Record<string, UserFilterConfig> = {
  status: {
    key: 'status',
    backendField: 'user_id',
    type: 'exists',
    name: 'Status',
    replacesDefaultFilter: true,
    staticOptions: [
      { id: 'Registered', label: 'Registered' },
      { id: 'Not signed up yet', label: 'Not signed up yet' },
      { id: 'Removed', label: 'Removed' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      // For "all except", we need to handle status exclusion
      // Since statuses are mutually exclusive, we compute what remains
      if (rule === 'allExcept') {
        const excludedStatuses = new Set(values.map((v) => v.id));
        const allStatuses = ['Registered', 'Not signed up yet', 'Removed'];
        const remainingStatuses = allStatuses.filter((status) => !excludedStatuses.has(status));

        // Return conditions for the remaining statuses (what to include)
        // These will be ORed together since statuses are mutually exclusive
        return remainingStatuses.map((status) => {
          if (status === 'Registered') return { user_id: { exists: true } };
          if (status === 'Not signed up yet') return { user_id: { exists: false } };
          if (status === 'Removed') return { deletedAt: { exists: true } };
          return {};
        });
      } else {
        // For "no except" (default), return normal conditions
        return values.map((v) => {
          if (v.id === 'Registered') return { user_id: { exists: true } };
          if (v.id === 'Not signed up yet') return { user_id: { exists: false } };
          if (v.id === 'Removed') return { deletedAt: { exists: true } };
          return {};
        });
      }
    },
  },

  role: {
    key: 'role',
    backendField: 'roles',
    type: 'enum',
    name: 'Role',
    staticOptions: [
      { id: 'Administrator', label: 'Administrator' },
      // SEE-1223: labels match useRoleLabel's i18n strings for the two
      // SEE-946 roles; on paidols without the flag they simply match no rows.
      { id: 'Secondary admin', label: 'Secondary admin' },
      { id: 'Approval admin', label: 'Approval admin' },
      { id: 'Cardholder', label: 'Cardholder' },
      { id: 'Group administrator', label: 'Group administrator' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'eq' : 'ne';
      return values.map((v) => {
        if (v.id === 'Administrator') {
          return { roles: { [operator]: Role.ADMINISTRATOR } };
        }
        if (v.id === 'Secondary admin') {
          return { roles: { [operator]: Role.SECONDARY_ADMINISTRATOR } };
        }
        if (v.id === 'Approval admin') {
          return { roles: { [operator]: Role.APPROVAL_ADMINISTRATOR } };
        }
        if (v.id === 'Cardholder') {
          return { roles: { [operator]: Role.CARDHOLDER } };
        }
        if (v.id === 'Group administrator') {
          return { roles: { [operator]: Role.GROUP_ADMINISTRATOR } };
        }
        return { roles: { [operator]: v.id } };
      });
    },
  },

  name: {
    key: 'name',
    backendField: 'firstName', // Note: Matches full names (first_name + last_name) on backend
    type: 'string',
    name: 'Name',
    supportsExclude: true,
  },

  email: {
    key: 'email',
    backendField: 'email',
    type: 'string',
    name: 'Email',
    supportsExclude: true,
  },

  username: {
    key: 'username',
    backendField: 'username',
    type: 'string',
    name: 'Username',
    supportsExclude: true,
  },

  visibilitySettings: {
    key: 'visibilitySettings',
    backendField: 'visibilitySettings',
    type: 'visibility',
    name: 'Visibility settings',
    staticOptions: [
      { id: 'Full access', label: 'Full access' },
      { id: 'GL code restrictions only', label: 'GL code restrictions only' },
      { id: 'Project restrictions only', label: 'Project restrictions only' },
      { id: 'GL code and project restrictions', label: 'GL code and project restrictions' },
    ],
  },

  projectVisibility: {
    key: 'projectVisibility',
    backendField: 'projectVisibility',
    type: 'string',
    name: 'Project visibility',
    supportsExclude: true,
    searchType: 'projects',
  },

  glCodeVisibility: {
    key: 'glCodeVisibility',
    backendField: 'glCodeVisibility',
    type: 'string',
    name: 'GL code visibility',
    supportsExclude: true,
    searchType: 'glCodes',
  },
};

const buildDefaultConditions = (
  filter: ColumnFilter,
  config: UserFilterConfig
): Record<string, unknown>[] => {
  const rule = filter.rule || 'noExcept';
  const operator = rule === 'noExcept' ? 'eq' : 'ne';
  const selectedValues = filter.selectedValues || [];

  return selectedValues.map((value) => ({
    [config.backendField]: { [operator]: value.id },
  }));
};

export const buildUserFilterQuery = (columnFilters: ColumnFilterMap): FilterBuildResult => {
  const andConditions: Array<{ or?: Array<Record<string, unknown>>; and?: Array<Record<string, unknown>> }> =
    [];
  let replacesDefault = false;

  Object.entries(columnFilters).forEach(([key, filter]) => {
    if (!filter.selectedValues?.length) return;

    const config = USER_FILTERS[key];
    if (!config) return;

    if (config.replacesDefaultFilter) {
      replacesDefault = true;
    }

    const rule = filter.rule || 'noExcept';
    const conditions = config.transform
      ? config.transform(filter.selectedValues, rule)
      : buildDefaultConditions(filter, config);

    if (conditions.length > 0) {
      // Always use OR - the backend Python filtering handles exclusion correctly
      // For status filters with allExcept, the transform computes remaining statuses
      // For other filters with allExcept, the transform returns ne conditions which are processed correctly
      andConditions.push({ or: conditions });
    }
  });

  return { andConditions, replacesDefault };
};

export const generateUserFilterObject = (columnFilters: ColumnFilterMap) => {
  const { andConditions, replacesDefault } = buildUserFilterQuery(columnFilters);

  if (andConditions.length === 0) {
    return undefined;
  }

  return {
    and: andConditions,
    _replacesDefault: replacesDefault,
  };
};

export interface DynamicFilterOptions {
  name?: FilterOption[];
  email?: FilterOption[];
  username?: FilterOption[];
  projectVisibility?: FilterOption[];
  glCodeVisibility?: FilterOption[];
}

const mapConfigTypeToColumnFilterType = (
  configType: UserFilterConfig['type']
): 'string' | 'number' | 'date' => {
  if (configType === 'exists' || configType === 'visibility' || configType === 'enum') {
    return 'string';
  }
  return configType;
};

export const buildColumnFiltersFromConfig = (
  pageName: string,
  dynamicOptions: DynamicFilterOptions = {}
): ColumnFilterMap => {
  return Object.fromEntries(
    Object.entries(USER_FILTERS).map(([key, config]) => [
      key,
      {
        key: config.key,
        type: mapConfigTypeToColumnFilterType(config.type),
        name: config.name,
        rule: 'noExcept',
        page: pageName,
        options: config.staticOptions || dynamicOptions[key as keyof DynamicFilterOptions] || [],
        selectedValues: [],
        searchType: config.searchType,
      } as ColumnFilter,
    ])
  );
};
